import { type ParsedUrlQuery } from 'querystring';

import { parseTrackingId } from '@/utils/analytics/trackingId';

import type {
  SchoolFunnelContext,
  SchoolSelectionEvent,
} from '../consts/events.quiz-school-package';
import type { PaidPlanType } from '../consts/paymentIntent';

export const getSchoolCountryCode = (country?: string, userCountry?: string) =>
  country || userCountry || 'UNKNOWN';

export const getSchoolFunnelContext = ({
  countryCode,
  available,
  source,
  checkoutId,
  impressionId,
}: {
  countryCode: string;
  available: boolean;
  source: string;
  checkoutId?: unknown;
  impressionId?: unknown;
}): SchoolFunnelContext => ({
  country_code: countryCode,
  region_policy:
    countryCode === 'KR' ? 'kr' : available ? 'nonkr_priced' : 'nonkr_unpriced',
  source,
  cta_context: 'school_package',
  ...(parseTrackingId(checkoutId) && {
    checkout_id: parseTrackingId(checkoutId),
  }),
  ...(parseTrackingId(impressionId) && {
    impression_id: parseTrackingId(impressionId),
  }),
});

export function getSchoolPackageTracking({
  countryCode,
  priceStatus,
  schoolPackageMode,
  selectedPlan,
  userCount,
  monthCount,
  minUsers,
  minMonths,
}: {
  countryCode?: string;
  priceStatus: SchoolSelectionEvent['school_price_status'];
  schoolPackageMode: boolean;
  selectedPlan: PaidPlanType;
  userCount: number;
  monthCount: number;
  minUsers?: number;
  minMonths?: number;
}): Pick<
  SchoolSelectionEvent,
  'region_policy' | 'school_price_status' | 'school_package_mode'
> {
  const isKR = countryCode === 'KR';
  const available = priceStatus === 'available';
  const on = isKR
    ? selectedPlan === 'PRO' &&
      minUsers !== undefined &&
      minMonths !== undefined &&
      userCount >= minUsers &&
      monthCount >= minMonths
    : schoolPackageMode;
  return {
    region_policy: isKR ? 'kr' : available ? 'nonkr_priced' : 'nonkr_unpriced',
    school_price_status: priceStatus,
    school_package_mode: available ? (on ? 'on' : 'off') : 'unavailable',
  };
}

export const getPaymentFunnelQuery = (
  query: ParsedUrlQuery,
): Record<string, string> => {
  const params: Record<string, string> = {};
  for (const key of ['checkout_id', 'impression_id']) {
    const id = parseTrackingId(query[key]);
    if (id) params[key] = id;
  }
  if (query.school === 'true') params.school = 'true';
  if (
    typeof query.school_country === 'string' &&
    /^[A-Z]{2}$/.test(query.school_country)
  )
    params.school_country = query.school_country;
  return params;
};
